import type { PulseEvent, PulseEventContext, PulseExternalIds, PulseExternalRef } from './types.js';

/**
 * The entity fields of a PulseEvent — what the event is *about*.
 *
 * Cinema-website programming falls into two cases and every call site has to handle both, so the
 * choice lives here rather than being re-decided per component (CIN-14405):
 *
 * - **film-backed** (`movieId` present) → sent as `movie_id`, which the API maps to
 *   `entity_type: 'movie'`. This feeds the four interest aggregate tables.
 * - **everything else** — special screenings, events, other programming → sent as
 *   `entity_type: 'content'`.
 *
 * Getting the first case wrong here is not fatal: ingestion re-resolves a content to its movie when
 * one exists, so a film-backed interaction is attributed to the film even if this component had no
 * `movieId` to hand (several call sites take `content` as an optional prop). What ingestion cannot
 * invent is a movie for genuinely film-less programming — those events reach `interest_event` and no
 * aggregate. See `docs/adr/0001-stop-recording-interest-in-non-film-content.md`.
 *
 * Passing neither is not representable: `contentId` is required, so an event always has a subject.
 * A caller who has no Cineamo id at all sets no entity fields and sends {@link externalRef} instead.
 */
export function trackedEntity({
    movieId,
    contentId
}: {
    movieId?: number | null;
    contentId: number;
}): Pick<PulseEvent, 'movie_id' | 'entity_type' | 'entity_id'> {
    if (movieId) {
        return { movie_id: movieId };
    }

    return { entity_type: 'content', entity_id: contentId };
}

/** What a call site actually holds: ids that may be numeric, and gaps where a system has no id. */
type LooseExternalIds = Record<string, string | number | null | undefined>;

function normalizeIds(ids: LooseExternalIds | undefined): PulseExternalIds | undefined {
    if (!ids) return undefined;

    const out: PulseExternalIds = {};
    for (const [system, id] of Object.entries(ids)) {
        if (id === null || id === undefined || id === '') continue;
        out[system] = String(id);
    }

    return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * The context fragment carrying a partner's own identifiers, for a surface that cannot name its
 * subject with a Cineamo id.
 *
 * The counterpart to {@link trackedEntity} for partner surfaces. It sets **no** entity fields,
 * because there is no Cineamo entity to name yet: the event reaches `interest_event` and no
 * aggregate until the server-side matching pass resolves these ids. That is the limit
 * `docs/adr/0015` accepts knowingly, and the reason these ids must travel from the very first event
 * — matching can resolve whatever is in the payload and can never invent what was left out.
 *
 * Returns a context *fragment* rather than a `context` object, so it composes with the rest of an
 * event's context instead of replacing it:
 *
 * ```ts
 * track({
 *     event_type: 'showtime_view',
 *     context: { page: 'programme', ...externalRef({ showing: { winticket: performanceId } }) }
 * });
 * ```
 *
 * Ids are coerced to strings and empty groups are dropped, so a call site can pass whatever it has
 * — including a number, or a system whose id is missing on this particular page — without producing
 * `{ cinema: {} }` for the matching pass to sift through. A ref with nothing usable in it yields an
 * empty fragment, which is harmless to spread.
 */
export function externalRef(ref: {
    cinema?: LooseExternalIds;
    entity?: LooseExternalIds;
    showing?: LooseExternalIds;
}): PulseEventContext {
    const external: PulseExternalRef = {};

    const cinema = normalizeIds(ref.cinema);
    if (cinema) external.cinema = cinema;

    const entity = normalizeIds(ref.entity);
    if (entity) external.entity = entity;

    const showing = normalizeIds(ref.showing);
    if (showing) external.showing = showing;

    return Object.keys(external).length > 0 ? { external } : {};
}
